import React, { useEffect, useMemo, useState } from "react";
import ReactFlow, {
  Background,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
  Controls,
} from "reactflow";
import "reactflow/dist/style.css";
import CustomNode from "./CustomNode";
import PropTypes from "prop-types";
import AddSubDialog from "components/AddSubDialog";
import { importData } from "services/api";
import { updateAllEmployees } from "services/api";

function EmployeeOrgChart({ allEmps, jobs, setAllEmps, canEdit }) {
  const [employees, setEmployees] = useState([]);
  const [addedSubs, setAddedSubs] = useState([]);
  const [removedSubs, setRemovedSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noChildren, setNoChildren] = useState([]);
  const [addSubDialogOpen, setAddSubDialogOpen] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [expandedManagers, setExpandedManagers] = useState(new Set());

  const { fitView } = useReactFlow();

  useEffect(() => {
    if (employees.length === 0) {
      // Initial load: just set root
      const rootEmp = allEmps.find((emp) => emp.role === 1);
      if (rootEmp) {
        setEmployees([rootEmp]);
      }
    } else {
      setEmployees((prev) => {
        let updated = [...prev];

        // 1. Remove employees if queued
        if (removedSubs.length > 0) {
          const removedIds = new Set(removedSubs.map((e) => e.id));
          updated = updated.filter((e) => !removedIds.has(e.id));
        }

        // 2. Refresh existing employees with updated data from allEmps
        const currentIds = new Set(updated.map((e) => e.id));
        updated = allEmps.filter((e) => currentIds.has(e.id));

        // 3. Add employees if queued
        if (addedSubs.length > 0) {
          const existingIds = new Set(updated.map((e) => e.id));
          const newOnes = addedSubs.filter((e) => !existingIds.has(e.id));
          updated = [...updated, ...newOnes];
        }

        return updated;
      });
    }

    // clear the queues after applying
    if (addedSubs.length > 0) setAddedSubs([]);
    if (removedSubs.length > 0) setRemovedSubs([]);
  }, [allEmps, addedSubs, removedSubs]);
  // Zoom to fit on employee update
  useEffect(() => {
    if (employees.length > 0) {
      setTimeout(() => {
        fitView({ padding: 0.2 });
      }, 100); // ensure nodes are rendered before zooming
    }
  }, [employees, fitView]);

  const { nodes, edges, nodeTypes } = useMemo(() => {
    const handleNodeClick = async (event, id, isOpen) => {
      event.stopPropagation();
      if (loading) return;

      setLoading(true);

      const managerId = id;

      if (isOpen) {
        // If open, remove all subordinates of this employee (and optionally their children too)
        const getAllSubordinates = (managerId) => {
          const directSubs = allEmps.filter((e) => e.directManagerId === managerId);
          const allSubs = [...directSubs];
          for (const sub of directSubs) {
            allSubs.push(...getAllSubordinates(sub.id));
          }
          return allSubs;
        };

        const subordinatesToRemove = getAllSubordinates(managerId);
        const idsToRemove = new Set(subordinatesToRemove.map((e) => e.id));

        setEmployees((prev) => prev.filter((e) => !idsToRemove.has(e.id)));
      } else {
        // Expand node: add direct children
        const children = allEmps.filter((emp) => emp.directManagerId === managerId);

        if (children.length === 0) {
          setNoChildren((prev) => [...prev, id]);
        } else {
          const existingIds = new Set(employees.map((e) => e.id));
          const uniqueChildren = children.filter((child) => !existingIds.has(child.id));
          setEmployees((prev) => [...prev, ...uniqueChildren]);
        }
      }

      setLoading(false);
    };
    const topLevelEmployees = employees?.filter((emp) => !emp.directManagerId) || [];

    const childMap = new Map();
    const nodeMap = new Map();

    employees?.forEach((emp) => {
      if (emp.directManagerId) {
        if (!childMap.has(emp.directManagerId)) {
          childMap.set(emp.directManagerId, []);
        }
        childMap.get(emp.directManagerId).push(emp);
      }
    });

    const calculatePositions = (empId, x, y) => {
      const children = childMap.get(empId) || [];
      const numChildren = children.length;

      if (numChildren === 0) {
        nodeMap.set(empId, { x, y });
        return { x, width: 180 };
      }

      let totalWidth = 0;
      const childPositions = [];

      children.forEach((child) => {
        const childResult = calculatePositions(child.id, x + totalWidth, y + 120);
        totalWidth += childResult.width + 50;
        childPositions.push(childResult);
      });

      const parentX = x + totalWidth / 2 - 90;
      nodeMap.set(empId, { x: parentX, y });
      return { x: parentX, width: totalWidth };
    };

    let currentX = 0;
    topLevelEmployees.forEach((emp) => {
      const result = calculatePositions(emp.id, currentX, 50);
      currentX += result.width + 100;
    });
    const nodes =
      employees?.map((emp) => ({
        id: emp.id.toString(),
        type: "custom",
        data: {
          label: emp.name,
          photo: emp.photo,
          id: emp.id.toString(),
          directManagerId: emp.directManagerId,
          hasChildren: childMap.has(emp.id) && childMap.get(emp.id).length > 0,
          noChildren: !allEmps.some((e) => e.directManagerId === emp.id),
          nodeClick: handleNodeClick,
          jobTitle: jobs.find((e) => e.jobId === emp.jobId).jobName || null,
        },
        position: nodeMap.get(emp.id),
        style: {
          cursor: "pointer",
          border: "none",
          background: "transparent",
          padding: 0,
        },
      })) || [];

    const nodeIds = new Set(employees?.map((emp) => emp.id.toString()));

    const edges =
      employees
        ?.filter((emp) => emp.directManagerId)
        .map((emp, index) => ({
          id: `e-${index}`,
          source: emp.directManagerId.toString(),
          target: emp.id.toString(),
          type: "smoothstep",
          animated: true,
          markerEnd: {
            type: MarkerType.ArrowClosed,
          },
        }))
        .filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target)) || [];

    const nodeTypes = {
      custom: CustomNode,
    };
    return { nodes, edges, nodeTypes };
  }, [employees, noChildren]);

  const handleAddSubordinates = (event, node) => {
    if (!canEdit) return;
    const ownId = node.id;

    // Exclude self, root-level (role === 1), and their own manager (to prevent loops)
    const filteredEmps = allEmps.filter(
      (e) => e.id !== ownId && e.role !== 1 && e.id !== node.data.directManagerId
    );

    // Preselect current subordinates
    const currentSubs = allEmps.filter((e) => e.directManagerId === ownId);

    setSelectedManagerId(ownId);
    setFilteredEmployees(filteredEmps);
    setSelectedEmployees(currentSubs); // ✅ preselect subordinates
    setAddSubDialogOpen(true);
  };

  const handleSave = async () => {
    // 1) Resolve selected manager and their (future) indirect manager for new subs
    const manager = allEmps.find((e) => e.id === selectedManagerId);
    if (!manager) return;
    const newIndirectManagerId = manager.directManagerId ?? null;

    // 2) Build sets for quick membership checks
    const desiredIds = new Set(
      selectedEmployees.map((e) => e.id).filter((id) => id !== selectedManagerId) // guard: never add self
    );
    const currentSubs = allEmps.filter((e) => e.directManagerId === selectedManagerId);
    const currentIds = new Set(currentSubs.map((e) => e.id));

    // 3) Diff
    const addedIds = new Set([...desiredIds].filter((id) => !currentIds.has(id)));
    const removedIds = new Set([...currentIds].filter((id) => !desiredIds.has(id)));

    // Optional: who exactly changed (for UI/logs)
    const addedEmployees = allEmps.filter((e) => addedIds.has(e.id));
    const removedEmployees = allEmps.filter((e) => removedIds.has(e.id));

    // 4) Build updated allEmps in one pass
    const updatedAllEmps = allEmps.map((emp) => {
      if (addedIds.has(emp.id)) {
        return {
          ...emp,
          directManagerId: selectedManagerId,
          indirectManagerId: newIndirectManagerId,
        };
      }
      if (removedIds.has(emp.id)) {
        return {
          ...emp,
          directManagerId: null,
          indirectManagerId: null,
        };
      }
      return emp;
    });

    // 5) Build DB payload only for changed employees (minimal fields recommended)
    const dbUpdatePayload = [
      ...[...addedIds].map((id) => ({
        id,
        directManagerId: selectedManagerId,
        indirectManagerId: newIndirectManagerId,
      })),
      ...[...removedIds].map((id) => ({
        id,
        directManagerId: null,
        indirectManagerId: null,
      })),
    ];

    // If nothing changed, just close
    if (dbUpdatePayload.length === 0) {
      onClose();
      return;
    }

    setUpdateLoading(true);
    try {
      // Persist
      const dbRes = await updateAllEmployees(dbUpdatePayload);
      if (dbRes !== "error") {
        // Update local state
        setAllEmps(updatedAllEmps);
        setEmployees((prev) => [...prev]); // force re-render if needed

        // For your awareness/debug/UX:
        // If you keep state for these, you could:
        setAddedSubs(addedEmployees);
        setRemovedSubs(removedEmployees);
      }
    } finally {
      setUpdateLoading(false);
      onClose();
    }
  };

  const onClose = () => {
    setAddSubDialogOpen(false);
    setSelectedJobId(null);
    setSelectedEmployees([]);
  };

  return (
    <div style={{ width: "100%", height: "100vh" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={handleAddSubordinates}
        fitView
        minZoom={0.1}
        zoomOnScroll
      >
        <Background variant="dots" gap={12} size={1} />
        <Controls />
      </ReactFlow>
      <AddSubDialog
        open={addSubDialogOpen}
        onClose={onClose}
        jobs={jobs}
        filteredEmployees={filteredEmployees}
        selectedJobId={selectedJobId}
        setSelectedJobId={setSelectedJobId}
        selectedEmployees={selectedEmployees}
        setSelectedEmployees={setSelectedEmployees}
        selectedManagerId={selectedManagerId}
        allEmps={allEmps}
        setAllEmps={setAllEmps}
        setEmployees={setEmployees}
        handleSave={handleSave}
        loading={updateLoading}
      />
    </div>
  );
}

EmployeeOrgChart.propTypes = {
  allEmps: PropTypes.array.isRequired,
  jobs: PropTypes.array.isRequired,
  setAllEmps: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default EmployeeOrgChart;
